import { defaultAllSelection } from "services/constants/selections";
import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import DateRangePicker from "react-bootstrap-daterangepicker";
import DateRangeIcon from "@mui/icons-material/DateRange";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { useSelector } from "react-redux";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { useFetch } from "services/hooks/useFetch";
import { showToast } from "services/utils/Status";
import { CustomAutoComplete } from "components/CustomComponents";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import {
  GlobalFieldLabel,
  globalTextFieldSx,
} from "components/DesignSystem/sharedUiPatterns";
import { SelectedEmployeeHeader } from "../SelectedEmployeeHeader";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import {
  FORM_ACTION_BUTTON_WIDTH,
  designColors,
} from "services/constants/designTokens";
import {
  SCHEDULE_REQUEST_TYPES,
  normalizeWeekOffLabel,
} from "pages/MyZone/MyZoneApply/ShiftCorrection/shiftCorrectionHelpers";

const SHIFT_CHANGE_API = "team_zone/approve/shift_change/teamzone-shift-change/";

const emptyForm = {
  selectedUser: null,
  requestedShift: "",
  requestedShiftId: "",
  reason: "",
};

const toInputDate = (value) =>
  value && moment(value).isValid() ? moment(value).format("YYYY-MM-DD") : "";

const fromInputDate = (value) =>
  value ? moment(value, "YYYY-MM-DD", true) : null;

const isRealShift = (row) =>
  Boolean(
    row?.shift_id &&
      Number(row.shift_id) !== defaultAllSelection &&
      row?.shift_name !== "Weekoff" &&
      row?.shift_name !== "Unassigned",
  );

// Same GlobalSidePanel shell as Roster's "Edit Shift Details" panel
// (TeamZone/PlanShift/Daily|Weekly|Monthly.js), reused here for a manager to
// raise a shift change request on an employee's behalf. Roster's panel opens
// from an already-selected roster row, so it already knows the employee and
// their current shift; this one has no row context, so Employee Name is a
// real picker (same list/employee + CustomAutoCompleteWithLogo pattern every
// other Team Zone Approve "Apply" screen uses), and Assigned Shift is
// resolved the same way My Zone's own shift-change apply panel
// (MyZoneApply/CorrectionShared/CorrectionApplyPanel.js) resolves it for the
// logged-in employee — just keyed off the picked employee instead of
// `userDetails`. Assigned Shift is never a manual field there (or here): it's
// looked up from daily_user_shift_assignments and shown read-only.
export const ApplyShiftChange = ({
  open,
  onClose,
  setReCallApi,
  expanded,
  onToggleExpand,
}) => {
  const { domain, userDetails, topbarCompanyId, userScreenAccess } =
    useSelector((state) => state.tokenReducer);
  const { result: employees } = useFetch(
    `${domain}list/employee?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
  );
  // Editor-only submissions must stay pending; approval requires approver access.
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Schedule Change",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Schedule Change",
    ) ||
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Shift Change",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Shift Change",
    );

  const [form, setForm] = useState(emptyForm);
  const [requestType, setRequestType] = useState("shift_change");
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [swapDate, setSwapDate] = useState(null);
  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);
  const [shiftOptions, setShiftOptions] = useState([]);
  const [shiftOptionsLoading, setShiftOptionsLoading] = useState(false);
  const [assignedShiftRows, setAssignedShiftRows] = useState([]);
  const [assignedShift, setAssignedShift] = useState(null);
  const [swapShift, setSwapShift] = useState(null);
  const [assignedShiftLoading, setAssignedShiftLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  const dateRangeLabel =
    dateRange.start && dateRange.end
      ? `${dateRange.start.format("DD/MM/YYYY")} - ${dateRange.end.format("DD/MM/YYYY")}`
      : "";
  const isWeekOffSwap = requestType === "week_off_swap";
  const originalIsWeekOff =
    isWeekOffSwap && normalizeWeekOffLabel(assignedShift?.name) === "Week Off";
  const swapDateMatchesOriginal =
    isWeekOffSwap &&
    dateRange.start &&
    swapDate &&
    swapDate.isSame(dateRange.start, "day");

  // Requested Shift should always show the full shift list rather than being
  // narrowed by a "Shift Based On" toggle.
  useEffect(() => {
    if (!dateRange.start || isWeekOffSwap) {
      setShiftOptions([]);
      return undefined;
    }

    let cancelled = false;
    const fetchShiftOptions = async () => {
      setShiftOptionsLoading(true);
      const response = await customGetAPI(`${domain}all/shift-filter/`);
      if (cancelled) return;
      const rows = Array.isArray(response?.responseDetails)
        ? response.responseDetails
        : (response?.responseDetails?.results ?? []);
      const options = response?.ok
        ? rows
            .map((item) => ({ ...item, label: item?.shift_name }))
        : [];
      setShiftOptions(options);
      setShiftOptionsLoading(false);
    };

    fetchShiftOptions();
    return () => {
      cancelled = true;
    };
  }, [domain, dateRange.start, isWeekOffSwap]);

  // Resolve the employee's currently assigned shift(s) across the selected
  // range — same daily_user_shift_assignments lookup CorrectionApplyPanel
  // uses for My Zone's own shift-change apply flow, just for the picked
  // employee instead of the logged-in user. A multi-day range can carry a
  // different shift per date (see the roster payload — 11-08 was HALFNIGHT,
  // 12-08 was General), so every date in range is kept for display; only the
  // *start* date's shift is submitted as `assigned_shift_id`, since the POST
  // contract only accepts one. 99999 / Weekoff / Unassigned all mean "no
  // shift on that date".
  useEffect(() => {
    if (!form.selectedUser?.user_id || !dateRange.start) {
      setAssignedShiftRows([]);
      setAssignedShift(null);
      setSwapShift(null);
      return undefined;
    }

    let cancelled = false;
    const fetchAssignedShift = async () => {
      setAssignedShiftLoading(true);
      const endMoment = isWeekOffSwap
        ? swapDate || dateRange.start
        : dateRange.end || dateRange.start;
      const fromDate = moment.min(dateRange.start, endMoment).format("YYYY-MM-DD");
      const toDate = moment.max(dateRange.start, endMoment).format("YYYY-MM-DD");
      const response = await customGetAPI(
        `${domain}daily_user_shift_assignments/?selected_user_id=${form.selectedUser.user_id}&from_date=${fromDate}&to_date=${toDate}&company_id=${topbarCompanyId}`,
      );
      if (cancelled) return;
      const rows = response?.responseDetails?.data;
      const shifts =
        response?.ok && Array.isArray(rows) && rows[0]?.shifts ? rows[0].shifts : [];
      setAssignedShiftRows(shifts);

      const target = dateRange.start.format("DD-MM-YYYY");
      const match = shifts.find((s) => s.date === target);
      setAssignedShift(
        isRealShift(match)
          ? { id: match.shift_id, name: match.shift_name }
          : match
            ? { id: null, name: normalizeWeekOffLabel(match.shift_name) || "Week Off" }
            : null,
      );
      if (isWeekOffSwap && swapDate) {
        const swapTarget = swapDate.format("DD-MM-YYYY");
        const swapMatch = shifts.find((s) => s.date === swapTarget);
        setSwapShift(
          isRealShift(swapMatch)
            ? { id: swapMatch.shift_id, name: swapMatch.shift_name }
            : null,
        );
      } else {
        setSwapShift(null);
      }
      setAssignedShiftLoading(false);
    };

    fetchAssignedShift();
    return () => {
      cancelled = true;
    };
  }, [
    domain,
    form.selectedUser?.user_id,
    dateRange.start,
    dateRange.end,
    isWeekOffSwap,
    swapDate, topbarCompanyId
  ]);

  const updateUser = (event, data) => {
    setForm((prev) => ({
      ...prev,
      selectedUser: data || null,
      requestedShift: "",
      requestedShiftId: "",
    }));
    setAssignedShiftRows([]);
    setAssignedShift(null);
    setSwapShift(null);
    setShowAutoCompleteAvathar(!data);
  };

  const updateRequestedShift = (event, data) => {
    setForm((prev) => ({
      ...prev,
      requestedShift: data?.shift_name || "",
      requestedShiftId: data?.shift_id || "",
    }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setRequestType("shift_change");
    setDateRange({ start: null, end: null });
    setSwapDate(null);
    setShiftOptions([]);
    setAssignedShiftRows([]);
    setAssignedShift(null);
    setSwapShift(null);
    setShowValidation(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isValid =
    Boolean(form.selectedUser?.user_id) &&
    Boolean(dateRange.start) &&
    Boolean(isWeekOffSwap ? dateRange.start : dateRange.end) &&
    Boolean(isWeekOffSwap ? swapDate : true) &&
    Boolean(isWeekOffSwap ? originalIsWeekOff : true) &&
    Boolean(isWeekOffSwap ? !swapDateMatchesOriginal : true) &&
    Boolean(isWeekOffSwap ? swapShift?.id : form.requestedShiftId) &&
    Boolean(isWeekOffSwap ? true : assignedShift?.id) &&
    form.reason.trim().length >= 3;

  const handleSubmit = async () => {
    if (!isValid) {
      setShowValidation(true);
      return;
    }
    setSubmitting(true);
    const requestedShiftId = isWeekOffSwap ? swapShift?.id : form.requestedShiftId;
    const startDate = dateRange.start.format("YYYY-MM-DD");
    const endDate = isWeekOffSwap
      ? dateRange.start.format("YYYY-MM-DD")
      : dateRange.end.format("YYYY-MM-DD");
    const payload = {
      ...(!hasApproverAccess && { status: "Pending" }),
      request_type: requestType,
      user_id: form.selectedUser.user_id,
      start_date: startDate,
      end_date: endDate,
      swap_date: isWeekOffSwap ? swapDate.format("YYYY-MM-DD") : null,
      assigned_shift_id: isWeekOffSwap ? swapShift?.id : assignedShift?.id || null,
      requested_shift_id: requestedShiftId,
      reason: form.reason.trim(),
    };
    const response = await customAPI(`${domain}${SHIFT_CHANGE_API}`, "POST", payload);
    if (response?.ok) {
      showToast("success", response?.responseDetails?.message || "Schedule change request created.");
      setReCallApi(true);
      handleClose();
    } else {
      showToast("error", response?.message || "Couldn't create the schedule change request. Please try again.");
    }
    setSubmitting(false);
  };

  const panelFooter = (
    <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end">
      <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
      <PrimaryButton
        onClick={handleSubmit}
        loading={submitting}
        disabled={submitting}
        sx={{ minWidth: FORM_ACTION_BUTTON_WIDTH }}
      >
        {hasApproverAccess ? "Approve" : "Apply"}
      </PrimaryButton>
    </Mui.Stack>
  );

  return open ? (
    <GlobalSidePanel
      onClose={handleClose}
      open={open}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      title="Apply Schedule Change"
      subtitle="Raise a schedule change request on behalf of an employee."
      bodySx={{ px: 3, py: 2.5 }}
      footer={panelFooter}
    >
      <Mui.Stack spacing={2.75}>

        <Mui.Box>
          <GlobalFieldLabel helperText="Choose whether this request changes a shift or swaps an employee's week off.">
            Request Type
          </GlobalFieldLabel>
          <Mui.Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
              gap: 1.25,
            }}
          >
            {Object.values(SCHEDULE_REQUEST_TYPES).map((type) => {
              const active = requestType === type.key;
              const Icon =
                type.key === "week_off_swap"
                  ? CalendarMonthOutlinedIcon
                  : SwapHorizOutlinedIcon;
              return (
                <Mui.Box
                  key={type.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setRequestType(type.key);
                    setForm((prev) => ({
                      ...prev,
                      requestedShift: "",
                      requestedShiftId: "",
                    }));
                    setDateRange({ start: null, end: null });
                    setSwapDate(null);
                    setAssignedShiftRows([]);
                    setAssignedShift(null);
                    setSwapShift(null);
                    setShowValidation(false);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      setRequestType(type.key);
                    }
                  }}
                  sx={{
                    border: "1px solid",
                    borderColor: active ? type.color : designColors.slate200,
                    borderRadius: 1,
                    p: 1.5,
                    cursor: "pointer",
                    backgroundColor: active ? `${type.color}12` : designColors.white,
                  }}
                >
                  <Mui.Stack direction="row" spacing={1.25} alignItems="center">
                    <Icon sx={{ color: active ? type.color : designColors.slate500 }} />
                    <Mui.Box>
                      <Mui.Typography sx={{ fontWeight: 800, color: active ? type.color : designColors.slate900 }}>
                        {type.label}
                      </Mui.Typography>
                      <Mui.Typography sx={{ fontSize: 12.5, color: designColors.slate600 }}>
                        {type.key === "week_off_swap"
                          ? "Work on week off and move it"
                          : "Change to a different shift"}
                      </Mui.Typography>
                    </Mui.Box>
                  </Mui.Stack>
                </Mui.Box>
              );
            })}
          </Mui.Box>
        </Mui.Box>

        <Mui.Box>
          <GlobalFieldLabel helperText="Select the employee for whom the Schedule Change request is being created.">
            Employee Name
          </GlobalFieldLabel>
          <CustomAutoCompleteWithLogo
            dataList={employees?.map((obj) => ({
              ...obj,
              label: obj?.user_name,
              image: obj?.image_url,
            }))}
            selectedValue={form.selectedUser}
            updateDataFunction={updateUser}
            placeHolder="Select employee name"
            errorMessage={
              showValidation && !form.selectedUser?.user_id ? "Employee is required" : ""
            }
            textBoxError={showValidation && !form.selectedUser?.user_id}
            showAvathar={showAutoCompleteAvathar}
            setShowAutoCompleteAvathar={setShowAutoCompleteAvathar}
          />
        </Mui.Box>
        <SelectedEmployeeHeader selectedUser={form.selectedUser} />
        <Mui.Box>
          <GlobalFieldLabel
            helperText={
              isWeekOffSwap
                ? "Select the employee's original week off date."
                : "Select the date range for which the Schedule Change request applies."
            }
          >
            {isWeekOffSwap ? "Work On (Original Week Off)" : "Date Range"}
          </GlobalFieldLabel>
          {isWeekOffSwap ? (
            <Mui.TextField
              variant="outlined"
              type="date"
              value={toInputDate(dateRange.start)}
              onChange={(event) => {
                const nextDate = fromInputDate(event.target.value);
                setDateRange({ start: nextDate, end: nextDate });
              }}
              size="small"
              InputLabelProps={{ shrink: true }}
              fullWidth
              error={showValidation && !dateRange.start}
              helperText={
                showValidation && !dateRange.start
                  ? "Original week off date is required"
                  : showValidation && dateRange.start && !originalIsWeekOff
                    ? "Select a date currently scheduled as Week Off"
                  : ""
              }
              sx={globalTextFieldSx}
            />
          ) : (
            <DateRangePicker
              initialSettings={{
                autoUpdateInput: false,
                drops: "down",
                parentEl: "body",
                applyClass: "custom-apply-btn",
                cancelClass: "custom-cancel-btn",
                locale: { format: "DD/MM/YYYY" },
              }}
              onCallback={(start, end) =>
                setDateRange({ start: moment(start), end: moment(end) })
              }
            >
              <Mui.TextField
                variant="outlined"
                placeholder="Select a date range"
                value={dateRangeLabel}
                size="small"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <Mui.InputAdornment position="end">
                      <Mui.IconButton>
                        <DateRangeIcon />
                      </Mui.IconButton>
                    </Mui.InputAdornment>
                  ),
                }}
                fullWidth
                error={showValidation && (!dateRange.start || !dateRange.end)}
                helperText={
                  showValidation && (!dateRange.start || !dateRange.end)
                    ? "Date range is required"
                    : ""
                }
                sx={globalTextFieldSx}
              />
            </DateRangePicker>
          )}
        </Mui.Box>
        {isWeekOffSwap ? (
          <Mui.Box>
            <GlobalFieldLabel helperText="Select the working day that should become the replacement week off.">
              Move Week Off To
            </GlobalFieldLabel>
            <Mui.TextField
              variant="outlined"
              type="date"
              value={toInputDate(swapDate)}
              onChange={(event) => setSwapDate(fromInputDate(event.target.value))}
              size="small"
              InputLabelProps={{ shrink: true }}
              fullWidth
              error={showValidation && (!swapDate || !swapShift?.id)}
              helperText={
                showValidation && !swapDate
                  ? "Move week off date is required"
                  : showValidation && swapDateMatchesOriginal
                    ? "Move week off date must be different"
                  : showValidation && swapDate && !swapShift?.id
                    ? "Select a working day with an assigned shift"
                    : ""
              }
              sx={globalTextFieldSx}
            />
          </Mui.Box>
        ) : null}
        <Mui.Box>
          <GlobalFieldLabel helperText="Displays the shift(s) currently assigned to the employee for the selected date range. This field is automatically populated and cannot be edited.">
            {isWeekOffSwap ? "Schedule Preview" : "Assigned Shift"}
          </GlobalFieldLabel>
          <Mui.Box
            sx={{
              borderRadius: 1,
              border: "1px solid",
              borderColor:
                showValidation &&
                ((!isWeekOffSwap && !assignedShift?.id) ||
                  (isWeekOffSwap && dateRange.start && !originalIsWeekOff) ||
                  (isWeekOffSwap && swapDate && !swapShift?.id))
                  ? "error.main"
                  : "divider",
              backgroundColor: "action.hover",
              overflow: "hidden",
            }}
          >
            {assignedShiftLoading ? (
              <Mui.Typography sx={{ px: 1.75, py: 1, fontSize: 14, color: designColors.slate600 }}>
                Checking assigned shift...
              </Mui.Typography>
            ) : assignedShiftRows.length ? (
              assignedShiftRows.map((row, index) => (
                <Mui.Stack
                  key={row.date || index}
                  direction="row"
                  justifyContent="space-between"
                  sx={{
                    px: 1.75,
                    py: 0.85,
                    borderTop: index === 0 ? "none" : "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Mui.Typography sx={{ fontSize: 13.5, color: designColors.slate600 }}>
                    {moment(row.date, "DD-MM-YYYY").format("DD MMM YYYY")}
                  </Mui.Typography>
                  <Mui.Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                    {normalizeWeekOffLabel(row.shift_name) || "Unassigned"}
                  </Mui.Typography>
                </Mui.Stack>
              ))
            ) : (
              <Mui.Typography sx={{ px: 1.75, py: 1, fontSize: 14, color: designColors.slate600 }}>
                No shift assignment found for the selected employee and date range.
              </Mui.Typography>
            )}
          </Mui.Box>
          {!assignedShiftLoading && assignedShiftRows.length > 1 && !isWeekOffSwap ? (
            <Mui.Typography sx={{ pt: 0.5, fontSize: 12, color: designColors.slate600 }}>
              The request will use the shift assigned on the start date
              {assignedShift?.name ? ` (${assignedShift.name})` : ""}.
            </Mui.Typography>
          ) : null}
          {isWeekOffSwap && dateRange.start && swapDate ? (
            <Mui.Typography sx={{ pt: 0.5, fontSize: 12, color: designColors.slate600 }}>
              {normalizeWeekOffLabel(assignedShift?.name) || "Week Off"} will become {swapShift?.name || "the selected working shift"}, and {swapShift?.name || "the selected working day"} will become Week Off.
            </Mui.Typography>
          ) : null}
          {showValidation && !isWeekOffSwap && !assignedShift?.id ? (
            <Mui.Typography sx={{ pt: 0.5, color: "error.main", fontSize: 12 }}>
              Couldn't determine an assigned shift for this employee on the start date.
            </Mui.Typography>
          ) : null}
        </Mui.Box>
        {!isWeekOffSwap ? (
          <Mui.Box>
            <GlobalFieldLabel helperText="Select the shift the employee is requesting to move to.">
              Requested Shift
            </GlobalFieldLabel>
            <CustomAutoComplete
              dataList={shiftOptions}
              selectedValue={form.requestedShift}
              updateDataFunction={updateRequestedShift}
              placeHolder={shiftOptionsLoading ? "Loading shifts..." : "Select requested shift"}
              errorMessage={
                showValidation && !form.requestedShiftId ? "Requested shift is required" : ""
              }
              textBoxError={showValidation && !form.requestedShiftId}
            />
          </Mui.Box>
        ) : null}
        <Mui.Box>
          <GlobalFieldLabel helperText="Enter the reason for the Schedule Change request.">
            Reason
          </GlobalFieldLabel>
          <Mui.TextField
            multiline
            minRows={3}
            fullWidth
            placeholder="Enter a reason for the schedule change"
            value={form.reason}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, reason: event.target.value.slice(0, 255) }))
            }
            error={showValidation && form.reason.trim().length < 3}
            helperText={
              showValidation && form.reason.trim().length < 3
                ? "Reason must be at least 3 characters"
                : ""
            }
            sx={globalTextFieldSx}
          />
        </Mui.Box>
      </Mui.Stack>
    </GlobalSidePanel>
  ) : null;
};

export default ApplyShiftChange;
